"use client";

/**
 * ThemeContext: Manages light/dark theme and icon presets.
 * REQUIREMENTS: Public routes (program catalog, booking, terms, etc.) always use light mode
 * regardless of user preference. ThemeProvider forces light when pathname matches (public) layout.
 * Requirements summary: blocked browser storage should fall back to light/default
 * preferences instead of interrupting public auth and registration pages.
 */

import type React from "react";
import { createContext, useState, useContext, useEffect } from "react";
import { usePathname } from 'next/navigation';

import { createLogger } from '@/utils/logger/devLogger';
import {
  safeLocalStorageGetItem,
  safeLocalStorageSetItem,
} from '@/utils/storage/safeLocalStorage';

const logger = createLogger('ThemeContext');

/** Path prefixes for app/(public) routes. Public pages always use light mode. */
const PUBLIC_PATH_PREFIXES = [
  'terms', 'privacy', 'programs', 'auth', 'gyms', 'enrollment', 'booking',
  'register', 'register-tryout', 'payment', 'invoice-pay', 'store',
  'report-unauthorized-change',
];

function isPublicRoute(pathname: string): boolean {
  const first = pathname.split('/').filter(Boolean)[0];
  return first ? PUBLIC_PATH_PREFIXES.includes(first) : false;
}

type Theme = "light" | "dark";

type IconTheme = {
  weight: number;      // 100-700: icon stroke weight
  grade: number;       // -50 to 200: visual weight adjustment
  fill: number;        // 0 = outlined, 1 = filled
  style: 'outlined' | 'filled' | 'auto'; // auto = follows theme preference
};

type IconPreset = 'minimal' | 'standard' | 'bold' | 'energetic' | 'custom';

type ThemeContextType = {
  theme: Theme;
  iconTheme: IconTheme;
  iconPreset: IconPreset;
  toggleTheme: () => void;
  updateIconTheme: (updates: Partial<IconTheme>) => void;
  setIconPreset: (preset: IconPreset) => void;
  resetIconTheme: () => void;
};

const ThemeContext = createContext<ThemeContextType | undefined>(undefined);

// Icon theme presets
const iconPresets: Record<IconPreset, IconTheme> = {
  minimal: { weight: 300, grade: -25, fill: 0, style: 'outlined' },
  standard: { weight: 400, grade: 0, fill: 0, style: 'outlined' },
  bold: { weight: 500, grade: 25, fill: 0, style: 'outlined' },
  energetic: { weight: 600, grade: 50, fill: 0.5, style: 'auto' }, // Mix of filled/outlined
  custom: { weight: 400, grade: 0, fill: 0, style: 'outlined' },
};

const defaultIconTheme: IconTheme = iconPresets.minimal;

export const ThemeProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const pathname = usePathname();
  const [theme, setTheme] = useState<Theme>("light");
  const [iconTheme, setIconTheme] = useState<IconTheme>(defaultIconTheme);
  const [iconPreset, setIconPresetState] = useState<IconPreset>('minimal');
  const [isInitialized, setIsInitialized] = useState(false);

  const forceLight = pathname !== null && isPublicRoute(pathname);

  useEffect(() => {
    // This code will only run on the client side
    const savedTheme = safeLocalStorageGetItem("theme") as Theme | null;
    const savedIconTheme = safeLocalStorageGetItem("iconTheme");
    const savedIconPreset = safeLocalStorageGetItem("iconPreset") as IconPreset | null;

    // REQUIREMENTS: Mobile WebView pages (from MoreFloatingMenu) must respect app dark mode.
    // When opened from CheerVue mobile app with ?mobile=true&theme=dark|light, use URL theme
    // so WebView content matches the native app's theme. Persist to localStorage for consistency.
    let initialTheme: Theme = savedTheme || "light";
    if (typeof window !== "undefined") {
      const params = new URLSearchParams(window.location.search);
      const isMobileWebView = params.get("mobile") === "true";
      const urlTheme = params.get("theme") as Theme | null;
      if (isMobileWebView && (urlTheme === "dark" || urlTheme === "light")) {
        initialTheme = urlTheme;
        safeLocalStorageSetItem("theme", urlTheme);
      }
    }

    // Restore icon theme
    if (savedIconTheme) {
      try {
        const parsedIconTheme = JSON.parse(savedIconTheme);
        setIconTheme(parsedIconTheme);
      } catch (e) {
        logger.warn('Failed to parse saved icon theme, using default');
      }
    }

    // Restore icon preset
    if (savedIconPreset && iconPresets[savedIconPreset]) {
      setIconPresetState(savedIconPreset);
    }

    setTheme(initialTheme);
    setIsInitialized(true);
  }, []);

  useEffect(() => {
    if (isInitialized) {
      safeLocalStorageSetItem("theme", theme);
      // Public routes (program catalog, booking, terms, etc.) always use light mode.
      if (forceLight || theme === "light") {
        document.documentElement.classList.remove("dark");
      } else {
        document.documentElement.classList.add("dark");
      }
    }
  }, [theme, isInitialized, forceLight]);

  // Apply icon theme to CSS custom properties
  useEffect(() => {
    if (isInitialized) {
      safeLocalStorageSetItem("iconTheme", JSON.stringify(iconTheme));
      safeLocalStorageSetItem("iconPreset", iconPreset);
      
      // Apply icon theme to CSS custom properties
      const root = document.documentElement;
      root.style.setProperty('--icon-weight', iconTheme.weight.toString());
      root.style.setProperty('--icon-grade', iconTheme.grade.toString());
      root.style.setProperty('--icon-fill', iconTheme.fill.toString());
      root.style.setProperty('--icon-optical-size', '24'); // Set optical size based on context
    }
  }, [iconTheme, iconPreset, isInitialized]);

  const toggleTheme = () => {
    setTheme((prevTheme) => (prevTheme === "light" ? "dark" : "light"));
  };

  const updateIconTheme = (updates: Partial<IconTheme>) => {
    setIconTheme(prev => ({ ...prev, ...updates }));
    // Switch to custom preset when manually adjusting
    if (iconPreset !== 'custom') {
      setIconPresetState('custom');
    }
  };

  const setIconPreset = (preset: IconPreset) => {
    setIconPresetState(preset);
    if (preset !== 'custom') {
      setIconTheme(iconPresets[preset]);
    }
  };

  const resetIconTheme = () => {
    setIconTheme(defaultIconTheme);
    setIconPresetState('minimal');
  };

  return (
    <ThemeContext.Provider 
      value={{ 
        theme, 
        iconTheme,
        iconPreset,
        toggleTheme,
        updateIconTheme,
        setIconPreset,
        resetIconTheme 
      }}
    >
      {children}
    </ThemeContext.Provider>
  );
};

export const useTheme = () => {
  const context = useContext(ThemeContext);
  if (context === undefined) {
    throw new Error("useTheme must be used within a ThemeProvider");
  }
  return context;
};
